import circleCheckFilledIcon from "@tabler/icons/filled/circle-check.svg";
import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { cn } from "@/utils/cn";

import { Icon, Stack, Text } from "../ui";

const messages = defineMessages({
  feature: {
    id: "premium.subscription_dialog.feature",
    defaultMessage: "What you’ll get",
  },
});

const tiers = [
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.basic"
        defaultMessage="Basic"
      />
    ),
    id: "basic",
  },
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.premium"
        defaultMessage="Patriot"
      />
    ),
    id: "premium",
  },
];

const features = [
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.verification_badge"
        defaultMessage="Verification badge"
      />
    ),
    id: "verification-badge",
    tiers: { basic: false, premium: true },
  },
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.truth_plus_badge"
        defaultMessage="Truth+ badge"
      />
    ),
    id: "truth-plus-badge",
    tiers: { basic: false, premium: true },
  },
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.edit_truths"
        defaultMessage="Edit Truths"
      />
    ),
    id: "edit-truths",
    tiers: { basic: false, premium: true },
  },
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.scheduled_truths"
        defaultMessage="Scheduled Truths"
      />
    ),
    id: "scheduled-truths",
    tiers: { basic: false, premium: true },
  },
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.boosted_comments"
        defaultMessage="Boosted Comments"
      />
    ),
    id: "boosted-comments",
    tiers: { basic: false, premium: true },
  },
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.larger_on_demand_catalog"
        defaultMessage="Larger on-demand catalog"
      />
    ),
    id: "catalog",
    tiers: { basic: false, premium: true },
  },
  {
    name: (
      <FormattedMessage
        id="premium.subscription_dialog.premium_live_channels"
        defaultMessage="Premium live channels"
      />
    ),
    id: "premium-live-channels",
    tiers: { basic: false, premium: true },
  },
];

interface FeatureTableProps {
  tableClassName?: string;
  size?: "md" | "lg";
}

export default function FeatureTable(props: FeatureTableProps) {
  const { size = "md", tableClassName } = props;

  const intl = useIntl();

  function renderCheck() {
    return (
      <Icon
        src={circleCheckFilledIcon}
        className="text-primary-500 dark:text-primary-400"
      />
    );
  }

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      className={tableClassName}
    >
      <table className="w-full">
        <thead>
          <tr>
            <th
              className={cn(
                "px-4 py-2 text-left md:px-0 dark:border-gray-800",
                {
                  "border-b border-solid border-white": size === "lg",
                },
              )}
            >
              <Text theme="muted">{intl.formatMessage(messages.feature)}</Text>
            </th>
            {tiers.map((tier) => (
              <th
                key={tier.id}
                className={cn("px-4 py-2 md:px-0 dark:border-gray-800", {
                  "border-b border-solid border-white text-left": size === "lg",
                  "text-center": size === "md",
                })}
              >
                <Text
                  weight="bold"
                  theme={tier.id === "premium" ? "default" : "muted"}
                  className={cn({
                    "text-base": size === "md",
                    "text-xl! lg:text-3xl!": size === "lg",
                  })}
                >
                  {tier.name}
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => (
            <tr key={feature.id}>
              <td className="px-4 py-2 md:px-0">
                <Text>{feature.name}</Text>
              </td>
              <td className="px-4 py-2 md:px-0">
                <div
                  className={cn("flex", {
                    "justify-center": size === "md",
                    "justify-start": size === "lg",
                  })}
                >
                  {feature.tiers.basic ? renderCheck() : <Text>&ndash;</Text>}
                </div>
              </td>
              <td className="px-4 py-2 md:px-0">
                <div
                  className={cn("flex", {
                    "justify-center": size === "md",
                    "justify-start": size === "lg",
                  })}
                >
                  {feature.tiers.premium ? renderCheck() : <Text>&ndash;</Text>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Stack>
  );
}
